import React, { useMemo } from 'react'

import {
  Button,
  DataTable,
  DataTableColumn,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  SelectWrapper,
  TextField,
  Typography
} from '@barracuda-internal/bds-core'
import { Block } from '@barracuda-internal/bds-core/dist/Icons/Core'
import { Warning } from '@barracuda-internal/bds-core/dist/Icons/Feedback'

import inboundSettingsStyles from 'components/pages/inboundSettings/inboundSettingsStyles'
import { useFormatMessage } from 'lib/localization'
import { GeoPolicyStatus, LanguagePolicy } from 'types/Settings'
import { Cell } from 'components/libs/grid/cell'
import { TableText } from 'components/libs/layout/pages/TableText'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'
import {
  LANGUAGE_SELECT_NAMES,
  useLanguagePoliciesTableLogic,
  UseLanguagePoliciesTableLogicProps
} from 'components/pages/inboundSettings/regionalPolicies/useLanguagePoliciesTableLogic'
import { PageSubtitle, FormSpacer } from 'components/libs/layout/pages/pageLayout'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'

const BASE_I18N_KEY = 'ess.inbound_settings.regional_policies'
const BASE_INBOUND_I18N_KEY = 'ess.inbound_settings'
const DIALOG_I18N_KEY = 'ess.dialog'

const LanguagePoliciesTable = (props: UseLanguagePoliciesTableLogicProps) => {
  const isReadonly = useIsReadonly()
  const inboundClasses = inboundSettingsStyles()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatInboundMessage = useFormatMessage(BASE_INBOUND_I18N_KEY)
  const formatDialogText = useFormatMessage(DIALOG_I18N_KEY)

  const [state, eventHandlers] = useLanguagePoliciesTableLogic(props)

  return useMemo(
    () => (
      <Grid container>
        <PageSubtitle>{formatMessage('content_policies')}</PageSubtitle>
        <FormSpacer />
        <Grid container>
          <Typography variant="h3">{formatMessage('language_policies')}</Typography>
          <Button
            className={inboundClasses.bulkEdit}
            onClick={eventHandlers.onBulkEdit}
            variant="contained"
            size="small"
            data-testid="bulk-button"
            disabled={isReadonly}
          >
            {formatInboundMessage('bulk_edit')}
          </Button>
        </Grid>
        <DataTable
          sortable={{
            allowUnsort: false,
            mode: 'single'
          }}
          sort={state.sort}
          onSortChange={eventHandlers.handleSortChange}
          data={state.tableData}
        >
          <DataTableColumn
            sortable
            field={LANGUAGE_SELECT_NAMES.LANGUAGE}
            title={formatMessage('language')}
            cell={({ dataItem }: { dataItem: LanguagePolicy }) => (
              <Cell>
                {dataItem.id === '' && (
                  <SelectWrapper variant="outlined" error={!!state.error}>
                    <Select
                      variant="outlined"
                      className={inboundClasses.mediumDropdown}
                      name={LANGUAGE_SELECT_NAMES.LANGUAGE}
                      defaultValue={state.formState.language}
                      onChange={eventHandlers.handleOnSelectChange}
                      SelectDisplayProps={{ style: { padding: 8 } }}
                      data-testid="new-language-field"
                      disabled={isReadonly}
                    >
                      {state.languages.map(language => (
                        <MenuItem key={language} value={language}>
                          {formatMessage(language)}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{state.error && formatMessage(state.error)}</FormHelperText>
                  </SelectWrapper>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="language-field">
                    {formatMessage(dataItem.language)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field={LANGUAGE_SELECT_NAMES.POLICY}
            title={formatMessage('policy')}
            cell={({ dataItem }: { dataItem: LanguagePolicy }) => (
              <Cell>
                {dataItem.id === '' && (
                  <Select
                    variant="outlined"
                    className={inboundClasses.smallDropdown}
                    name={LANGUAGE_SELECT_NAMES.POLICY}
                    defaultValue={state.formState.policy}
                    onChange={eventHandlers.handleOnSelectChange}
                    SelectDisplayProps={{ style: { padding: 8 } }}
                    data-testid="new-policy-field"
                    disabled={isReadonly}
                  >
                    {Object.keys(GeoPolicyStatus).map(policyValue => (
                      <MenuItem key={policyValue} value={policyValue}>
                        {formatMessage(policyValue)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {dataItem.id !== '' && (
                  <Typography variant="body2" data-testid="policy-field" className={inboundClasses.flexWrapper}>
                    {dataItem.policy === GeoPolicyStatus.block && <Block className={inboundClasses.block} />}
                    {dataItem.policy === GeoPolicyStatus.quarantine && (
                      <Warning className={inboundClasses.quarantine} />
                    )}
                    {formatMessage(dataItem.policy as string)}
                  </Typography>
                )}
              </Cell>
            )}
          />
          <DataTableColumn
            sortable
            field="comment"
            title={formatMessage('comment')}
            cell={({ dataItem }: { dataItem: LanguagePolicy }) => (
              <Cell data-testid="comment-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <TextField
                    fullWidth
                    name="comment"
                    defaultValue={state.formState.comment}
                    inputRef={state.form.fields.comment.ref}
                    onChange={eventHandlers.handleOnInputChange}
                    inputProps={{ style: { padding: 8 } }}
                    data-testid="new-comment-field"
                    disabled={isReadonly}
                  />
                )}
                {dataItem.id && <TableText dataText={dataItem.comment} truncated dataTestId="comment-field" />}
              </Cell>
            )}
          />
          <DataTableColumn
            field="actions"
            title={formatMessage('actions')}
            cell={({ dataItem }: { dataItem: LanguagePolicy }) => (
              <Cell data-testid="action-cell" className={inboundClasses.smallCell}>
                {!dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onAdd()
                    }}
                    variant="text"
                    data-testid="add-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('add')}
                  </Button>
                )}
                {dataItem.id && (
                  <Button
                    color="primary"
                    onClick={() => {
                      eventHandlers.onRemove(dataItem.id)
                    }}
                    variant="text"
                    data-testid="remove-button"
                    disabled={isReadonly}
                  >
                    {formatMessage('remove')}
                  </Button>
                )}
              </Cell>
            )}
          />
        </DataTable>
        {state.isConfirmDialogOpen && (
          <WarningDialog
            title={formatDialogText('regional_policies.title')}
            description={formatDialogText('regional_policies.description')}
            onCancel={eventHandlers.onRemoveCancel}
            onConfirm={eventHandlers.onRemoveConfirm}
            cancelButtonLabel={formatDialogText('regional_policies.cancel')}
            confirmButtonLabel={formatDialogText('regional_policies.confirm')}
          />
        )}
      </Grid>
    ),
    [
      formatMessage,
      formatInboundMessage,
      formatDialogText,
      inboundClasses.bulkEdit,
      inboundClasses.mediumDropdown,
      inboundClasses.smallDropdown,
      inboundClasses.flexWrapper,
      inboundClasses.block,
      inboundClasses.quarantine,
      inboundClasses.smallCell,
      eventHandlers,
      isReadonly,
      state.sort,
      state.tableData,
      state.isConfirmDialogOpen,
      state.error,
      state.formState.language,
      state.formState.policy,
      state.formState.comment,
      state.languages,
      state.form.fields.comment.ref
    ]
  )
}

export default LanguagePoliciesTable
